/*
 * Lily & Minx - mega menu behaviour on top of Prestige (module lm-header)
 * LAST UPDATED: 25/09/2026
 * ---------------------------------------------------------------------------
 * The L&M desktop menu (snippets/lm-header-nav.liquid) reuses Prestige's
 * <mega-menu-disclosure> (the Shop panel) and <dropdown-menu-disclosure>
 * (the Need to know dropdown), which already give click mode, click on touch
 * screens, a second tap following the link, close on focus-out and editor
 * block selection. This adds behaviours from the design note "Header and
 * mega menu" through document-level listeners, so it survives theme-editor
 * section reloads. (a) and (b) apply to both kinds, (c) to panels only (a
 * dropdown does not dim the page). Vendor touch points
 * (tests/modules/lm-header.test.mjs asserts this exact set):
 * - the element's public `trigger` getter and `toggle()`;
 * - one deliberate exception: when switching panels, the old panel's
 *   <details> is closed directly (`details.open = false`) after toggle(false),
 *   see (a). It relies on Prestige's CustomDetails.close() returning early
 *   for a closed <details> and removing `open` only when its fade finishes
 *   (vendorProbes on assets/theme.js in module.json).
 *
 * a) Hover intent. Prestige opens a panel on the first mouseover. Here a panel
 *    opens only after the pointer rests on its top item for the header's
 *    "Hover delay" (data-lm-hover-delay, default 150ms), so it does not flash
 *    when the pointer passes over. A mouse click during the delay follows
 *    the item's link, as it would once Prestige had opened the panel. Moving
 *    to another item while a panel or dropdown is open switches at once.
 *    Keyboard, click and touch opens are never delayed.
 * b) Esc closes an open panel wherever focus is. Prestige's document handler
 *    looks for details[open] being a MenuDisclosure, which never matches, so
 *    Esc only worked with focus inside the panel. Focus returns to the top item
 *    when it was inside the panel.
 * c) In click mode (and on touch screens) a tap on the dimmed page closes the
 *    panel without also following whatever link is under the dim. Taps the
 *    dim does not cover (above the panel's top edge, or on a fixed layer
 *    painted above the header such as the privacy banner, a chat launcher or
 *    a dialog) reach their target as usual.
 * Without this script the native <details> and Prestige's behaviour still work.
 */

const HOST = '.lm-header-nav :is(mega-menu-disclosure, dropdown-menu-disclosure)';
const HOST_TAGS = 'mega-menu-disclosure, dropdown-menu-disclosure';
const SUMMARY = `${HOST} > details > summary`;
const DEFAULT_DELAY = 150;

let pending = null;

const isOpen = (host) => host.querySelector(':scope > details')?.getAttribute('aria-expanded') === 'true';
const openHosts = () => Array.from(document.querySelectorAll(HOST)).filter(isOpen);
const summaryOf = (event) => (event.target instanceof Element ? event.target.closest(SUMMARY) : null);

const delayFor = (host) => {
  const value = Number.parseInt(host.closest('.lm-header-nav')?.dataset.lmHoverDelay ?? '', 10);
  return Number.isFinite(value) && value >= 0 ? value : DEFAULT_DELAY;
};

const cancelPending = () => {
  if (!pending) return;
  clearTimeout(pending.timer);
  pending = null;
};

/* a) Hover intent: capture phase, so this runs before Prestige's listener on <details>. */
document.addEventListener('mouseover', (event) => {
  const summary = summaryOf(event);
  if (!summary) return;
  const host = summary.closest(HOST_TAGS);
  if (host.trigger !== 'hover' || isOpen(host)) return;

  const others = openHosts().filter((other) => other !== host);
  if (others.length) {
    /* Another panel is open: close it and let Prestige open this one immediately. The old panel is hidden
       at once rather than faded: the new one opens in the same place, and Prestige's own close timer for
       the old one (started when the pointer left it) would otherwise restart the fade from full opacity
       (its hide animation is opacity [1, 0]). With <details> closed, Prestige's close() returns early.
       This direct write is the one documented exception to the public-API rule (header comment). */
    cancelPending();
    others.forEach((other) => {
      other.toggle(false);
      other.querySelector(':scope > details').open = false;
    });
    return;
  }

  event.stopPropagation();
  if (pending?.host === host) return;
  cancelPending();
  pending = {
    host,
    timer: setTimeout(() => {
      pending = null;
      if (summary.matches(':hover') && !isOpen(host)) host.toggle(true);
    }, delayFor(host)),
  };
}, true);

/* While a panel is closed, leaving its top item cancels the pending open. Prestige's own close
   timer is not started for a closed panel, so it cannot close the panel after a delayed open. */
document.addEventListener('mouseout', (event) => {
  const summary = summaryOf(event);
  if (!summary) return;
  const host = summary.closest(HOST_TAGS);
  if (host.trigger !== 'hover' || isOpen(host)) return;
  if (pending?.host === host && !summary.contains(event.relatedTarget)) cancelPending();
  event.stopPropagation();
}, true);

/* A mouse click on an item during the hover delay: Prestige follows data-follow-link only once the panel is open,
   so without this the first click would open the panel instead of following the link, as it does once the hover
   has opened it. Keyboard activation (event.detail 0) and touch (click trigger) keep Prestige's toggle. */
document.addEventListener('click', (event) => {
  const summary = summaryOf(event);
  if (!summary || event.detail === 0) return;
  const host = summary.closest(HOST_TAGS);
  if (pending?.host !== host || host.trigger !== 'hover' || isOpen(host)) return;
  cancelPending();
  const link = summary.dataset.followLink;
  if (!link) return;
  event.preventDefault();
  event.stopPropagation();
  window.location.href = link;
}, true);

/* b) Esc from anywhere. */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  cancelPending();
  openHosts().forEach((host) => {
    const hadFocus = host.contains(document.activeElement);
    host.toggle(false);
    if (hadFocus) host.querySelector(':scope > details > summary')?.focus();
  });
});

/* c) A tap on the dimmed page closes the panel and does nothing else. */
const stackLevel = (element) => {
  const value = Number.parseInt(getComputedStyle(element).zIndex, 10);
  return Number.isFinite(value) ? value : 0;
};

/* The dim is the open panel's shadow, clipped at the panel's top edge and painted inside the header section's
   stacking context. A tap is outside it when it lands above that edge, in a dialog, or in a fixed layer whose
   z-index is above the header section's (Prestige's privacy banner is z-index 10, the header 4). */
const outsideDim = (event, hosts) => {
  const panelTop = Math.min(...hosts.map((host) => host.querySelector('.lm-mega')?.getBoundingClientRect().top ?? Infinity));
  if (event.clientY < panelTop) return true;
  if (event.target.closest('dialog, [role="dialog"], [aria-modal="true"]')) return true;
  const header = hosts[0].closest('.shopify-section--header');
  const headerLevel = header ? stackLevel(header) : 0;
  for (let element = event.target; element && element !== document.body; element = element.parentElement) {
    if (getComputedStyle(element).position === 'fixed') return stackLevel(element) > headerLevel;
  }
  return false;
};

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element) || event.target.closest('.shopify-section--header')) return;
  /* Only panels dim the page; an open dropdown is closed by Prestige's own click-outside handler. */
  const hosts = openHosts().filter((host) => host.trigger === 'click' && host.querySelector('.lm-mega'));
  if (!hosts.length || outsideDim(event, hosts)) return;
  event.preventDefault();
  event.stopPropagation();
  hosts.forEach((host) => host.toggle(false));
}, true);
